import { useCallback, useEffect, useMemo } from "react";

import { useNavigate, useSearchParams } from "react-router-dom-v5-compat";
import { useFintechUiStyles, useModuleBEM } from "@b2bportal/core-themes";
import type { ExternalResource } from "@b2bportal/core-types";
import {
  CallState,
  CoreFintechUiComponents,
  type ExternalResourceSuccess,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { useDeviceType } from "@b2bportal/core-utilities";
import {
  FintechMarketplaceSection,
  useFintechMarketplace,
} from "@components/core-fintech-ui";
import { useI18nContext } from "@hopper-b2b/i18n";
import { PageLoadingScreen } from "@tenant/ui";
import clsx from "clsx";

import { FintechMarketplaceProductsFetcher } from "../../components";

import {
  type FintechMarketplaceConfig,
  type FintechMarketplaceProductConfig,
  type FintechMarketplaceProductsSelection,
  FintechMarketplaceProductViewType,
} from "../../FintechMarketplaceConfig";

import defaultStyles from "./FintechMarketplacePage.module.scss";

export interface FintechMarketplacePageProps
  extends FintechMarketplaceConfig,
    PlatformComponentProps {
  goToPrevStep: () => void;
  goToNextStep: (
    productsSelection: FintechMarketplaceProductsSelection
  ) => void;
  onNoProductOffers: () => void;
}

export enum FintechMarketplacePageQueryParams {
  PRODUCT = "product",
}

export const FintechMarketplacePage = (props: FintechMarketplacePageProps) => {
  const [block, cn] = useModuleBEM(
    useFintechUiStyles(
      CoreFintechUiComponents.FintechMarketplacePage,
      defaultStyles
    ),
    CoreFintechUiComponents.FintechMarketplacePage
  );

  const { t } = useI18nContext();

  const {
    className,
    goToNextStep,
    onNoProductOffers,
    useInitProducts,
    PageWrapper,
    TripDetails,
    PriceBreakdown,
    productViewType = FintechMarketplaceProductViewType.STACK,
    productsBreadcrumbs,
  } = props;

  const {
    goBack,
    currentProductKey,
    onProductsSelectionChange,
    offeredProducts,
    isLoading,
    products,
    canContinue,
    productsSelection,
  } = useFintechMarketplacePageState(props);

  useEffect(
    function goToNextStepWhenNoOffers() {
      if (isLoading) {
        return;
      }

      if (!currentProductKey) {
        onNoProductOffers();
        return;
      }
    },
    [isLoading, products, currentProductKey, onNoProductOffers]
  );

  return (
    <FintechMarketplaceProductsFetcher useInitProducts={useInitProducts}>
      {isLoading || !currentProductKey ? (
        <PageLoadingScreen
          className={cn("loading-screen")}
          isLoading
          text={t(
            "core-fintech-ui.fintech-marketplace.productOffersFetchLoadingTitle"
          )}
        />
      ) : (
        <PageWrapper className={clsx(block, className)} onBack={goBack}>
          <div className={cn("container")}>
            <div className={cn("section")}>
              <FintechMarketplaceSection
                canContinue={canContinue}
                offeredProducts={offeredProducts}
                currentProductKey={currentProductKey}
                onProductsSelectionChange={onProductsSelectionChange}
                onContinue={goToNextStep}
                TripDetails={TripDetails}
                PriceBreakdown={PriceBreakdown}
                productViewType={productViewType}
                productsBreadcrumbs={productsBreadcrumbs}
                productsSelection={productsSelection}
              />
            </div>
          </div>
        </PageWrapper>
      )}
    </FintechMarketplaceProductsFetcher>
  );
};

export const useFintechMarketplacePageState = (
  props: FintechMarketplacePageProps
) => {
  const { isDesktopAndUp } = useDeviceType();

  const {
    goToPrevStep: goToPrevNavStep,
    goToNextStep: goToNextNavStep,
    productViewType,
  } = props;

  const {
    products,
    productsOrder,
    canContinue,
    productOffers,
    isProductOffersFetching,
    productsSelection,
  } = useFintechMarketplace(props);

  const allProductsWithOffers = useMemo(
    () =>
      productsOrder.map((key) => ({
        key,
        offers: productOffers[key],
        View: products[key],
      })),
    [productOffers, products, productsOrder]
  );

  const loadedProductEntries = allProductsWithOffers.filter(
    isExternalResourceSuccess
  );

  const offeredProducts = useMemo(
    () =>
      new Map(
        loadedProductEntries.map(({ key, View, offers }) => [
          key,
          {
            View,
            offers: offers.data,
          },
        ])
      ),
    [loadedProductEntries]
  );

  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const currentProductKey: string | undefined =
    searchParams.get(FintechMarketplacePageQueryParams.PRODUCT) ??
    loadedProductEntries[0]?.key;

  const currentProductIndex = useMemo(() => {
    return loadedProductEntries.findIndex(
      ({ key }) => key === currentProductKey
    );
  }, [loadedProductEntries, currentProductKey]);

  const autoGoToNextStepOnContinue =
    !isDesktopAndUp ||
    productViewType === FintechMarketplaceProductViewType.INDIVIDUAL;

  const onProductsSelectionChange = useCallback(
    (productsSelection: FintechMarketplaceProductsSelection) => {
      if (currentProductIndex >= loadedProductEntries.length - 1) {
        if (autoGoToNextStepOnContinue) {
          goToNextNavStep(productsSelection);
        }
        return;
      }

      setSearchParams((params) => {
        params.set(
          FintechMarketplacePageQueryParams.PRODUCT,
          loadedProductEntries[currentProductIndex + 1].key
        );

        return params;
      });
    },
    [
      currentProductIndex,
      goToNextNavStep,
      autoGoToNextStepOnContinue,
      loadedProductEntries,
      setSearchParams,
    ]
  );

  const goBack = useCallback(() => {
    if (currentProductIndex === 0) {
      goToPrevNavStep();
      return;
    }

    navigate(-1);
  }, [goToPrevNavStep, navigate, currentProductIndex]);

  return {
    goBack,
    currentProductKey,
    onProductsSelectionChange,
    products,
    offeredProducts,
    isLoading: isProductOffersFetching,
    canContinue,
    productsSelection,
  };
};

function isExternalResourceSuccess<T extends ReadonlyArray<unknown>>(item: {
  key: string;
  offers: ExternalResource<T>;
  View: FintechMarketplaceProductConfig;
}): item is {
  key: string;
  offers: ExternalResourceSuccess<T>;
  View: FintechMarketplaceProductConfig;
} {
  return item.offers.state === CallState.Success && item.offers.data.length > 0;
}
